/**
 * Shared scheduled-workflow health check.
 *
 * A `schedule_trigger` workflow is "unhealthy" when its most recent scheduled
 * fire time has passed (beyond a small buffer) and no run happened at or after
 * it. This is the single source of truth for that decision, shared by:
 *   - the frontend workflows list (the red/green trigger indicator), and
 *   - the backend `detectMissedScheduledWorkflows` job (which alerts Sentry).
 *
 * Deliberately does NOT parse the cron expression. `cron-parser` must stay out
 * of `@erp/workflows` so it is never bundled into the Temporal workflow sandbox
 * (this package's barrel is imported by the workflow VM). Each caller computes
 * `previousScheduled` with its own `cron-parser` and passes it in.
 */

/** Grace after the expected fire time before a never-run schedule is flagged. */
const NEVER_RAN_BUFFER_MS = 60 * 1000;

/** Grace after the expected fire time before a missed run is flagged. */
const MISSED_RUN_BUFFER_MS = 30 * 1000;

export interface ScheduleHealthInput {
  /**
   * The most recent PAST scheduled fire time for the cron, or `null` if the
   * cron expression could not be parsed. Callers compute this from the cron
   * expression + timezone (e.g. `cron-parser`'s `.prev()`).
   */
  previousScheduled: Date | null;
  /** ISO timestamp of the most recent qualifying run, or `undefined` if none. */
  lastRunTime: string | undefined;
  /** Whether the workflow is active (and, where relevant, pinned). */
  isActive: boolean;
  /** The moment the check is evaluated ("now"). */
  now: Date;
}

export interface ScheduleHealthResult {
  healthy: boolean;
  reason?: string;
}

/**
 * Decide whether a scheduled workflow has missed its expected run.
 *
 * Healthy (no alert) when: inactive, cron unparseable, still within the buffer
 * after the expected fire, or a run happened at/after the last expected fire.
 * Unhealthy when the last (qualifying) run predates the most recent expected
 * fire and the buffer has elapsed.
 */
export function evaluateScheduleHealth({
  previousScheduled,
  lastRunTime,
  isActive,
  now,
}: ScheduleHealthInput): ScheduleHealthResult {
  // Inactive (or unpinned) workflows are not expected to run.
  if (!isActive) {
    return { healthy: true, reason: "Workflow is inactive" };
  }

  if (!previousScheduled) {
    return { healthy: true, reason: "Could not parse cron expression" };
  }

  if (!lastRunTime) {
    const expectedWithBuffer = new Date(
      previousScheduled.getTime() + NEVER_RAN_BUFFER_MS,
    );
    if (now > expectedWithBuffer) {
      return {
        healthy: false,
        reason: `Expected run at ${previousScheduled.toLocaleString()} but never ran`,
      };
    }
    return { healthy: true, reason: "Waiting for first scheduled run" };
  }

  const lastRun = new Date(lastRunTime);
  const expectedWithBuffer = new Date(
    previousScheduled.getTime() + MISSED_RUN_BUFFER_MS,
  );

  if (lastRun < previousScheduled && now > expectedWithBuffer) {
    return {
      healthy: false,
      reason: `Expected run at ${previousScheduled.toLocaleString()} but last ran at ${lastRun.toLocaleString()}`,
    };
  }

  return { healthy: true };
}
